<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("BLACK_LIST_USERS.BLACK_LIST_USER_LIST") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openBlackListUserCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_BLACK_LIST_USERS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{
                $t("BLACK_LIST_USERS.ADD_BLACK_LIST_USER")
              }}</span>
            </base-button>
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_BLACK_LIST_USERS)"
              :objectType="'blackListUser'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <black-list-user-list-table
        @onViewBlackListUser="openBlackListUserViewModal"
        @onEditBlackListUser="openBlackListUserEditModal"
        @onDeleteBlackListUser="deleteBlackListUser"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewBlackUserListModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewBlackUserListModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'BLACK_LIST_USER_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("BLACK_LIST_USERS.VIEW_BLACK_LIST_USER") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openBlackListUser"
                :objectType="'blackListUsers'"
                :objectId="openBlackListUser.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="edit" @click="openBlackListUserEditModal(openBlackListUser)">
                  <i class="fal fa-edit"></i>
                  <span>{{ $t("COMMON.EDIT") }}</span>
                </button>
                <button class="edit" @click="deleteBlackListUser(openBlackListUser)">
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeBlackListUserModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-black-list-user-page v-if="openBlackListUser" :blackListUserId="openBlackListUser.id" />
          </div>
        </div>
      </div>

      <div
        v-if="isEditBlackListUserModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditBlackListUserModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'BLACK_LIST_USER'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("BLACK_LIST_USERS.EDIT_BLACK_LIST_USER") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="view" @click="openBlackListUserViewModal(openBlackListUser)">
                  <i class="fal fa-eye"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeBlackListUserModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-black-list-user-page
              v-if="openBlackListUser"
              :blackListUserId="openBlackListUser.id"
              @onViewBlackListUser="openBlackListUserViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddBlackListUserModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddBlackListUserModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'BLACK_LIST_USER'">
          <div class="resizable-wrapper-content-header">
            <h1>
              {{ $t("BLACK_LIST_USERS.ADD_BLACK_LIST_USER") }}
            </h1>
            <button class="close" @click="closeBlackListUserModal">
              <i class="fal fa-times"></i>
            </button>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-black-list-user-page @onViewBlackListUser="openBlackListUserViewModal" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import BlackListUserListTable from "./partials/BlackListUserListTable.vue";
import EditBlackListUserPage from "./components/EditBlackListUserComponent.vue";
import AddBlackListUserPage from "./components/AddBlackListUserComponent.vue";
import ViewBlackListUserPage from "./components/ViewBlackListUserComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    BlackListUserListTable,
    NotificationSubscription,
    EditBlackListUserPage,
    AddBlackListUserPage,
    ViewBlackListUserPage,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const blackListUserId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewBlackUserListModalOpened = false;
    let isEditBlackListUserModalOpened = false;
    let isAddBlackListUserModalOpened = false;
    let openBlackListUser = null;
    if (blackListUserId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewBlackUserListModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditBlackListUserModalOpened = true;
      }
      openBlackListUser = { id: blackListUserId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddBlackListUserModalOpened = true;
    }
    return {
      isViewBlackUserListModalOpened: isViewBlackUserListModalOpened,
      isEditBlackListUserModalOpened: isEditBlackListUserModalOpened,
      isAddBlackListUserModalOpened: isAddBlackListUserModalOpened,
      openBlackListUser: openBlackListUser,
      renderKey: 1,
    };
  },

  methods: {
    openBlackListUserCreateModal() {
      this.closeBlackListUserModal();
      this.isAddBlackListUserModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List BlackListUsers",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openBlackListUserViewModal(blackListUser, reRender = false) {
      this.closeBlackListUserModal();
      this.openBlackListUser = blackListUser;
      this.isViewBlackUserListModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List BlackListUsers",
          query: { id: this.openBlackListUser.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openBlackListUserEditModal(blackListUser) {
      this.closeBlackListUserModal();
      this.openBlackListUser = blackListUser;
      this.isEditBlackListUserModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List BlackListUsers",
          query: { id: this.openBlackListUser.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeBlackListUserModal() {
      this.isAddBlackListUserModalOpened = false;
      this.isViewBlackUserListModalOpened = false;
      this.isEditBlackListUserModalOpened = false;
      this.openBlackListUser = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List BlackListUsers",
          query: {},
        }).href
      );
    },

    async deleteBlackListUser(blackListUser) {
      const confirmation = await swal.fire({
        title: this.$t("BLACK_LIST_USERS.DELETE_THIS_BLACK_LIST_USER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("blackListUsers/destroy", blackListUser.id);
          this.renderKey++;
          this.closeBlackListUserModal();
          this.$notify({
            type: "success",
            message: this.$t("BLACK_LIST_USERS.BLACK_LIST_USER_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
